// Captura del IMEI o la serie de una pieza: tecleo, lector de código de barras o cámara (IMEI), con
// sugerencias de piezas disponibles de ese producto en la tienda desde el 4.º carácter (REQ-18).
import { lazy, Suspense, useId, useState } from 'react';
import { Barcode } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { ApiError } from '@/api/errors';
import { cn } from '@/lib/cn';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { MIN_IDENTIFIER_PREFIX, useSuggestIdentifiers } from '../../api/useWizard';
import { lineErrorMessage } from '../../lineErrors';
import { IMEI_LENGTH, normalizeIdentifier, type SerializedMode } from '../../deliveryPieces';

// El escáner arrastra el .wasm de zxing: entra al bundle solo cuando se abre.
const ImeiScanner = lazy(() => import('../ImeiScanner').then((m) => ({ default: m.ImeiScanner })));

const SUGGEST_DEBOUNCE_MS = 300;
const SUGGEST_FALLBACK =
    'No pudimos traer sugerencias. Teclea o escanea el identificador completo.';
const INPUT =
    'block h-9 w-full max-w-[260px] rounded-md border border-input bg-background px-2.5 font-mono text-[14px] leading-[1.4] tracking-[0.06em] tabular-nums outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_2px_hsl(var(--ring)/0.15)]';

const PLACEHOLDER: Record<SerializedMode, string> = {
    IMEI: '357123456789012',
    SERIAL: 'Número de serie o ICCID',
};

/** Un inventario caído no es culpa de lo tecleado: se sigue a mano. */
function suggestErrorOf(error: unknown): string {
    if (error instanceof ApiError && error.httpStatus === 502) return SUGGEST_FALLBACK;
    return lineErrorMessage(error) ?? SUGGEST_FALLBACK;
}

export interface IdentifierFieldProps {
    sessionId: string;
    /** Sin línea (sesión anterior a las líneas) no hay sugerencias. */
    lineId?: string;
    sku?: string;
    mode: SerializedMode;
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
}

export function IdentifierField({
    sessionId,
    lineId,
    sku,
    mode,
    label,
    value,
    onChange,
    error,
    disabled = false,
}: IdentifierFieldProps) {
    const inputId = useId();
    const listboxId = useId();
    const [scannerOpen, setScannerOpen] = useState(false);
    const [picked, setPicked] = useState<string | null>(null);
    const prefix = useDebounce(value, SUGGEST_DEBOUNCE_MS);
    const suggestions = useSuggestIdentifiers(sessionId, lineId ?? '', sku ?? '', prefix);

    const set = (raw: string): void => {
        setPicked(null);
        onChange(normalizeIdentifier(mode, raw));
    };
    const pick = (identifier: string): void => {
        setPicked(identifier);
        onChange(identifier);
    };

    const searchable = !!lineId && !!sku && value.length >= MIN_IDENTIFIER_PREFIX;
    const settled = searchable && prefix === value && !suggestions.isFetching;
    const options = (suggestions.data ?? []).filter((s) => s !== value);
    const open = settled && picked !== value && options.length > 0;
    const empty = settled && suggestions.isSuccess && suggestions.data.length === 0;
    const suggestMessage =
        settled && suggestions.isError ? suggestErrorOf(suggestions.error) : null;

    return (
        <div className="flex flex-col gap-1.5">
            <label
                htmlFor={inputId}
                className="block text-[10px] uppercase tracking-[0.06em] text-muted-foreground"
            >
                {label} · {mode === 'IMEI' ? `IMEI (${String(IMEI_LENGTH)} dígitos)` : 'serie'}
            </label>
            <div className="flex flex-wrap items-center gap-2">
                <input
                    id={inputId}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-invalid={error ? true : undefined}
                    inputMode={mode === 'IMEI' ? 'numeric' : 'text'}
                    autoComplete="off"
                    placeholder={PLACEHOLDER[mode]}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => {
                        set(e.target.value);
                    }}
                    className={cn(INPUT, error && 'border-destructive')}
                />
                {mode === 'IMEI' && (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={disabled}
                        leftIcon={<Barcode className="size-3.5" aria-hidden />}
                        onClick={() => {
                            setScannerOpen(true);
                        }}
                    >
                        Escanear
                    </Button>
                )}
            </div>
            {open && (
                <ul
                    id={listboxId}
                    role="listbox"
                    aria-label={`Piezas disponibles de ${label}`}
                    className="max-w-[260px] overflow-hidden rounded-md border border-border bg-popover shadow-sm"
                >
                    {options.map((identifier) => (
                        <li
                            key={identifier}
                            role="option"
                            aria-selected={false}
                            tabIndex={-1}
                            onClick={() => {
                                pick(identifier);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') pick(identifier);
                            }}
                            className="cursor-pointer px-2.5 py-1.5 font-mono text-[13px] tabular-nums hover:bg-accent"
                        >
                            {identifier}
                        </li>
                    ))}
                </ul>
            )}
            {empty && (
                <p className="text-[11px] text-muted-foreground">
                    {`No hay piezas disponibles de este producto que empiecen con «${value}».`}
                </p>
            )}
            {suggestMessage !== null && (
                <p className="text-[11px] text-warning">{suggestMessage}</p>
            )}
            {error && <p className="text-[11.5px] text-destructive">{error}</p>}
            {scannerOpen && (
                <Suspense fallback={<Loading size="sm" />}>
                    <ImeiScanner
                        onClose={() => {
                            setScannerOpen(false);
                        }}
                        onDetected={set}
                    />
                </Suspense>
            )}
        </div>
    );
}
